import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Authenticated } from "../../models/User";
import { Tenant } from "../../models/Tenant";
import { thunkFetchTenants } from "../../store/tenants/actions";

interface TenantMultiSelectProps extends Omit<SelectProps, "loading"> {
  currentUser?: Authenticated;
  excludeTenantIds?: Array<string>;
  label?: string;
  onSelected?: (value: ReadonlyArray<Tenant>) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<string>;
}

export const TenantMultiSelect: FunctionComponent<TenantMultiSelectProps> = ({
  excludeTenantIds,
  label = "Cluster",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const dispatch = useDispatch();
  const tenants =
    useSelector<AppState, ReadonlyArray<Tenant>>(s => s.tenants.list) || [];
  const [state, setState] = useState<ReadonlyArray<Tenant>>([]);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let clusterIds = event.target.value as Array<string>;
      let clusters = state.filter(cluster => clusterIds.includes(cluster.id));

      onSelected(clusters ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (clusterIds: ReadonlyArray<string>): ReactNode => {
    return state
      .filter(cluster => clusterIds.includes(cluster.id))
      .map(cluster => cluster.name)
      .join(", ");
  };

  useEffect(() => {
    let clusters =
      tenants &&
      tenants.length > 0 &&
      tenants
        .filter(
          tenant =>
            excludeTenantIds === undefined ||
            !excludeTenantIds.some(x => x === tenant.id)
        )
        .map(tenant => tenant);

    if (clusters) {
      setState(clusters.sort((a, b) => (a.name > b.name ? 1 : -1)));
    }
  }, [tenants]);

  return (
    <FormControl variant={variant} className={props.className}>
      <InputLabel
        id="cluster-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="cluster-multi-select-label"
        id="cluster-multi-select"
        autoWidth={true}
        disabled={props.disabled}
        input={<Input />}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(cluster => (
            <MenuItem key={cluster.id} value={cluster.id}>
              <Checkbox checked={value.indexOf(cluster.id) > -1} />
              <ListItemText primary={cluster.name} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
